import Joi from 'joi'
import clsx from 'clsx'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../../../UI/Btn'
import TextField from '../../../../../../UI/TextField'

import SettingHint from '../SettingHint'
import TemplateLinks from '../TemplateLinks'

import config from '../RequirementsConfig/style.module.scss'
import styles from '../style.module.scss'

export const CONTACT_FOLLOW_UP_DEFAULTS = { notifyOnAccept: true, followUp: true, contactDays: 14, askAgainDays: 7 }

const schema = Joi.object({
	supporterContactFollowUp: Joi.object().keys({
		notifyOnAccept: Joi.boolean(),
		followUp: Joi.boolean(),
		contactDays: Joi.number().integer().min(1).max(60).required().messages({
			'number.base': 'Bitte eine Frist in Tagen angeben.',
			'number.integer': 'Bitte eine Frist in ganzen Tagen angeben.',
			'number.min': 'Bitte eine Frist von mindestens 1 Tag angeben.',
			'number.max': 'Bitte eine Frist von höchstens 60 Tagen angeben.',
			'any.required': 'Bitte eine Frist in Tagen angeben.'
		}),
		askAgainDays: Joi.number().integer().min(1).max(30).required().messages({
			'number.base': 'Bitte einen Abstand in Tagen angeben.',
			'number.integer': 'Bitte einen Abstand in ganzen Tagen angeben.',
			'number.min': 'Bitte einen Abstand von mindestens 1 Tag angeben.',
			'number.max': 'Bitte einen Abstand von höchstens 30 Tagen angeben.',
			'any.required': 'Bitte einen Abstand in Tagen angeben.'
		})
	})
})

/**
 * Was nach der Zusage einer Wirtschaftsexpert:in passiert (Anforderung #232).
 *
 * Zwei Schalter und zwei Fristen: die Mitteilungen bei der Zusage (an
 * Schüler:innen und Lehrkräfte, dazu der Fristsatz in der E-Mail), und die
 * Rückfrage „Habt ihr schon Kontakt aufgenommen?" nach Ablauf der Frist. Die
 * Frist nennt auch die Anfrage selbst, bevor die Expert:in entscheidet.
 */
export default function SupporterContactFollowUp(props) {
	const { t } = useTranslation()
	const {
		register,
		reset,
		watch,
		handleSubmit,
		formState: { errors }
	} = useForm({ resolver: joiResolver(schema) })

	const notifyOnAccept = watch('supporterContactFollowUp.notifyOnAccept')
	const followUp = watch('supporterContactFollowUp.followUp')

	const onSubmit = form => {
		props
			.updateOptions({
				name: 'supporterContactFollowUp',
				value: {
					...(props.form?.supporterContactFollowUp || {}),
					notifyOnAccept: Boolean(form.supporterContactFollowUp.notifyOnAccept),
					followUp: Boolean(form.supporterContactFollowUp.followUp),
					contactDays: Number(form.supporterContactFollowUp.contactDays),
					askAgainDays: Number(form.supporterContactFollowUp.askAgainDays)
				}
			})
			.then(() => toast.success(t('Options was updated')))
	}

	useEffect(() => {
		const value = { ...CONTACT_FOLLOW_UP_DEFAULTS, ...(props.form?.supporterContactFollowUp || {}) }
		reset({
			supporterContactFollowUp: {
				notifyOnAccept: value.notifyOnAccept !== false,
				followUp: value.followUp !== false,
				contactDays: value.contactDays,
				askAgainDays: value.askAgainDays
			}
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.form])

	return (
		<form id="supporterContactFollowUp" className={styles.root} onSubmit={handleSubmit(onSubmit)}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>Nach der Zusage: Kontaktaufnahme</h3>
				<SettingHint
					example="Beispiel: Frist 14 Tage, erneut fragen nach 7 Tagen — sagt eine Expert:in am 1. März zu, fragt die Junior Datenbank am 15. März bei den Schüler:innen nach. Antworten sie „Noch nicht, in 3 Tagen“, kommt die nächste Frage am 18. März; antworten sie gar nicht, am 22. März."
					warning="Die Frist steht auch in der Anfrage an die Expert:in („meldet sich innerhalb von … Tagen“) und gilt für Zusagen ab dem Speichern. Bereits laufende Rückfragen behalten ihren Termin. Ausgeschaltet wird nicht mehr nachgefragt; die Frist bleibt in den Texten stehen."
				>
					Sagt eine Wirtschaftsexpert:in zu, ist die Junior Company am Zug: Sie meldet sich über den
					bevorzugten Kontaktweg der Expert:in. Bei der Zusage bekommen Schüler:innen und Lehrkräfte eine
					Mitteilung, die Schüler:innen zusätzlich eine E-Mail mit Frist und Kontaktdaten. Nach Ablauf der
					Frist fragt die Junior Datenbank die Schüler:innen mit einer Mitteilung, ob der Kontakt zustande
					gekommen ist. „Ja“ beendet die Rückfrage; bei „Noch nicht“ geben die Schüler:innen an, in wie vielen
					Tagen (1–14) sie sich melden, und werden dann erneut gefragt — so lange, bis ein „Ja“ kommt oder die
					Zusammenarbeit endet. Ist die Expert:in pausiert, wartet die Rückfrage.
				</SettingHint>
				<TemplateLinks
					items={[
						{ channel: 'email', key: 'SUPPORTER_NEW_REQUEST' },
						{ channel: 'email', key: 'SUPPORTER_REQUEST_ACCEPTED' },
						{ channel: 'internal', key: 'SUPPORTER_REQUEST_ACCEPTED' },
						{ channel: 'internal', key: 'SUPPORTER_REQUEST_ACCEPTED_TEACHER' },
						{ channel: 'internal', key: 'SUPPORTER_CONTACT_CHECK' }
					]}
				/>
				<div className={styles.sectionContent}>
					<label className={clsx(config.switch, styles.field)}>
						<input type="checkbox" {...register('supporterContactFollowUp.notifyOnAccept')} />
						<span className={config.track} aria-hidden="true" />
						<span className={config.state}>
							Mitteilung bei der Zusage: {notifyOnAccept ? t('activated') : t('deactivated')}
						</span>
					</label>
					<label className={clsx(config.switch, styles.field)}>
						<input type="checkbox" {...register('supporterContactFollowUp.followUp')} />
						<span className={config.track} aria-hidden="true" />
						<span className={config.state}>
							Rückfrage zur Kontaktaufnahme: {followUp ? t('activated') : t('deactivated')}
						</span>
					</label>
					<div className={styles.flexContainer}>
						<TextField
							className={styles.field}
							label="Kontaktaufnahme innerhalb von [x] Tagen"
							mb={20}
							register={register('supporterContactFollowUp.contactDays')}
							error={errors.supporterContactFollowUp?.contactDays}
						/>
						<TextField
							className={styles.field}
							label="Ohne Antwort erneut fragen nach [x] Tagen"
							mb={20}
							register={register('supporterContactFollowUp.askAgainDays')}
							error={errors.supporterContactFollowUp?.askAgainDays}
						/>
					</div>
					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}

SupporterContactFollowUp.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}
